"use client";

import { useState, useEffect } from "react";
import { Search, X } from "lucide-react";
import { motion } from "framer-motion";
import { getEmployees } from "../services/employeesApi";
import { Employee } from "../types/employee";
import { addQuickPayment } from "../services/BookingsApi";
import { useAuth } from "../auth/AuthContext";

interface Stylist {
  id: number;
  initials: string;
  name: string;
  lightColor: string;
  darkColor: string;
}

// Color scheme for employee avatars
const colorSchemes = [
  { light: "bg-[#E7E2F3]", dark: "bg-[#7E57C2]" },
  { light: "bg-[#F7EAF4]", dark: "bg-[#C2185B]" },
  { light: "bg-[#DDF4ED]", dark: "bg-[#29BF6A]" },
  { light: "bg-[#F9ECF8]", dark: "bg-[#EC4899]" },
];

// Generate initials from employee name
const getInitials = (name: string): string => {
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }
  return name.substring(0, 2).toUpperCase();
};

// Map employee to stylist format
const mapEmployeeToStylist = (employee: Employee, index: number): Stylist => {
  const colorScheme = colorSchemes[index % colorSchemes.length];
  return {
    id: employee.id,
    initials: getInitials(employee.name),
    name: employee.name,
    lightColor: colorScheme.light,
    darkColor: colorScheme.dark,
  };
};

export default function QuickPaymentPanel({
  onClose,
  onProceed,
  quickPaymentType = "stylist",
}: {
  onClose: () => void;
  onProceed: () => void;
  quickPaymentType?: "direct" | "stylist";
}) {
  const [search, setSearch] = useState("");
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [amount, setAmount] = useState("0.00");
  const [hasDecimal, setHasDecimal] = useState(false);
  const [decimalDigits, setDecimalDigits] = useState(0);
  const [stylists, setStylists] = useState<Stylist[]>([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [showLoadingOverlay, setShowLoadingOverlay] = useState(false);
  const { user } = useAuth();

  // Check if XE Connect is active
  const isPayWithXEConnect = user?.company?.pay_with_xeconnect === "active";

  // Fetch employees when in stylist mode
  useEffect(() => {
    if (quickPaymentType === "stylist") {
      const fetchEmployees = async () => {
        setLoading(true);
        try {
          const employees = await getEmployees();
          // Filter out employees with "Unnamed" and map to stylists
          const mappedStylists = employees
            .filter((emp) => emp.name && emp.name !== "Unnamed")
            .map((emp, index) => mapEmployeeToStylist(emp, index));
          setStylists(mappedStylists);
          // Auto-select first employee if available and none selected
          if (mappedStylists.length > 0) {
            setSelectedId((prev) => (prev === null ? mappedStylists[0].id : prev));
          }
        } catch (error) {
          console.error("Error fetching employees:", error);
          setStylists([]);
        } finally {
          setLoading(false);
        }
      };
      fetchEmployees();
    } else {
      // Clear stylists when not in stylist mode
      setStylists([]);
      setSelectedId(null);
    }
  }, [quickPaymentType]);

  const filtered = stylists.filter((s) =>
    s.name.toLowerCase().includes(search.toLowerCase())
  );

  // XE Connect payment functions
  const checkXEServerUp = async (): Promise<boolean> => {
    try {
      const response = await fetch("http://localhost:5050/api/Xepos/IsServerUp", {
        method: "GET",
      });
      return response.ok;
    } catch (error) {
      console.error("XE Connect server check failed:", error);
      return false;
    }
  };

  const processXEPayment = async (totalAmount: number): Promise<{ approved: boolean; transactionId?: string }> => {
    const uuid = Math.random().toString(36).substring(2, 7);

    try {
      // Check if server is up
      const isServerUp = await checkXEServerUp();
      if (!isServerUp) {
        alert("Please first run the Xe-Connect server!");
        return { approved: false };
      }

      // Process payment
      const response = await fetch("http://localhost:5050/api/Xepos/Evo/Websale", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          Amount: totalAmount,
          OrderNo: uuid,
        }),
      });

      const data = await response.json();

      if (data.StatusMessage === "Approved") {
        setSuccessMessage("Card payment is done successfully!");
        return { approved: true, transactionId: data.TransactionId };
      } else {
        alert("Card payment is cancelled/declined!");
        return { approved: false };
      }
    } catch (error) {
      console.error("XE Connect payment error:", error);
      alert("Something went wrong, please try again later!");
      return { approved: false };
    }
  };

  // Handle payment submission
  const handlePayment = async (paymentType: "cash" | "card") => {
    const amountValue = parseFloat(amount);

    if (amountValue <= 0) {
      alert("Please enter a valid amount");
      return;
    }

    setSubmitting(true);
    setSuccessMessage(null);

    try {
      let transactionId: string | null = null;

      // Check for card payment with XE Connect
      if (paymentType === "card" && isPayWithXEConnect) {
        setShowLoadingOverlay(true);
        try {
          const xeResult = await processXEPayment(amountValue);
          setShowLoadingOverlay(false);

          if (!xeResult.approved) {
            setSubmitting(false);
            return;
          }

          transactionId = xeResult.transactionId || null;
        } catch (error) {
          setShowLoadingOverlay(false);
          setSubmitting(false);
          return;
        }
      }

      // Make API call to store payment
      const payload = {
        emp_id: quickPaymentType === "stylist" && selectedId ? selectedId : null,
        amount: amountValue,
        payment_type: paymentType,
        trans_id: transactionId || null,
      };

      const response = await addQuickPayment(payload);

      if (response.status === "success") {
        setSuccessMessage(response.message || "Quick payment added successfully");
        // Reset form
        setAmount("0.00");
        setHasDecimal(false);
        setDecimalDigits(0);
        setSelectedId(quickPaymentType === "stylist" && stylists.length > 0 ? stylists[0].id : null);

        // Close panel after 2 seconds
        setTimeout(() => {
          setSuccessMessage(null);
          onClose();
        }, 2000);
      } else {
        alert(response.message || "Failed to add payment");
      }
    } catch (error: any) {
      console.error("Error adding quick payment:", error);
      alert(error.response?.data?.message || "Failed to add payment. Please try again.");
    } finally {
      setSubmitting(false);
      setShowLoadingOverlay(false);
    }
  };

  // -------------------------------
  // ✅ Corrected Keypad Logic
  // -------------------------------
  const handleKey = (key: string) => {
    setAmount((prev) => {
      let [intPart, decPart] = prev.split(".");
      decPart = decPart || "00";

      // DELETE key
      if (key === "del") {
        if (hasDecimal && decimalDigits > 0) {
          // delete decimal digits first
          decPart =
            decPart.slice(0, decimalDigits - 1) +
            "0".repeat(2 - (decimalDigits - 1));
          setDecimalDigits(decimalDigits - 1);
          if (decimalDigits - 1 === 0) setHasDecimal(false);
          return `${intPart}.${decPart}`;
        } else if (intPart.length > 1) {
          intPart = intPart.slice(0, -1);
          return `${parseInt(intPart)}.00`;
        } else {
          return "0.00";
        }
      }

      // DECIMAL key
      if (key === ".") {
        if (hasDecimal) return prev; // already has one
        setHasDecimal(true);
        setDecimalDigits(0);
        return `${intPart}.`;
      }

      // NUMBER keys
      if (/^\d$/.test(key)) {
        if (!hasDecimal) {
          // building integer part
          if (intPart === "0") intPart = key;
          else intPart += key;
          return `${parseInt(intPart)}.00`;
        } else {
          // building decimal part (max 2 digits)
          if (decimalDigits >= 2) return prev;
          const newDec = (decPart.slice(0, decimalDigits) + key).slice(0, 2);
          setDecimalDigits(decimalDigits + 1);
          return `${intPart}.${newDec}`;
        }
      }

      return prev;
    });
  };
  // -------------------------------

  return (
    <div className="fixed inset-0 flex justify-end z-[100]">
      {/* Loading Overlay for XE Connect */}
      {showLoadingOverlay && (
        <div className="fixed inset-0 bg-black/60 z-[70] flex items-center justify-center">
          <div className="bg-white rounded-lg p-6 flex flex-col items-center gap-4">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#885ABB]"></div>
            <p className="text-gray-700 font-medium">Processing card payment...</p>
          </div>
        </div>
      )}

      {/* Overlay */}
      <div className="absolute inset-0 bg-black/40 backdrop-blur-[2px]" onClick={onClose}></div>

      {/* Success Message */}
      {successMessage && (
        <div className="fixed top-4 left-1/2 transform -translate-x-1/2 z-[60] animate-fade-in">
          <div className="bg-[#22C55E] text-white px-6 py-4 rounded-lg shadow-lg flex items-center gap-3 min-w-[300px] max-w-md">
            <svg
              className="w-6 h-6 flex-shrink-0"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M5 13l4 4L19 7"
              />
            </svg>
            <span className="font-medium text-sm">{successMessage}</span>
          </div>
        </div>
      )}

      {/* Sliding Panel */}
      <div className={`relative bg-white h-full flex flex-col lg:flex-row transform transition-transform duration-300 ease-out translate-x-0 overflow-y-auto ${quickPaymentType === "direct"
        ? "w-full sm:w-[60%] lg:w-[40%] justify-center"
        : "w-full sm:w-[80%] lg:w-[60%]"
        }`}>
        {/* Close Button */}
        <motion.button
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
          onClick={onClose}
          className="absolute right-4 sm:right-6 lg:right-auto lg:-left-14 top-4 sm:top-6 lg:top-8 w-10 h-10 flex items-center justify-center rounded-full bg-white text-[#885ABB] shadow-lg lg:shadow-[-4px_0_12px_rgba(0,0,0,0.15)] hover:bg-[#402B69] hover:text-white transition-all z-[60] group"
          aria-label="Close"
        >
          <X size={20} strokeWidth={3} className="group-hover:rotate-90 transition-transform duration-300" />
        </motion.button>
        {/* Left Side - Stylists */}
        {quickPaymentType === "stylist" && (
          <div className="w-full lg:w-[38%] relative p-4 sm:p-6 lg:p-6 flex flex-col border-b lg:border-b-0 border-gray-100">
            <h2 className="text-[18px] sm:text-[20px] font-semibold text-[#402B69] pt-8 lg:pt-0 mb-4">
              Select Stylist
            </h2>

            {/* Search */}
            <div className="relative mb-6">
              <input
                type="text"
                placeholder="Search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full border border-[#E5E0EB] rounded-md py-2.5 pl-4 pr-10 text-[#402B69] text-[15px] font-medium focus:outline-none"
              />
              <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#885ABB]" />
            </div>

            {/* List */}
            <div className="space-y-3 overflow-y-auto flex-1 pr-2">
              {loading ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#885ABB]"></div>
                </div>
              ) : filtered.length === 0 ? (
                <div className="flex items-center justify-center py-8 text-gray-500">
                  No employees found
                </div>
              ) : (
                filtered.map((s) => {
                  const isSelected = selectedId === s.id;
                  return (
                    <div
                      key={s.id}
                      onClick={() => setSelectedId(s.id)}
                      className={`flex items-center gap-3 p-3 rounded-md border cursor-pointer transition
                      ${isSelected
                          ? "border-[#885ABB] bg-[#F9F9F9] shadow-sm"
                          : "border-[#E5E0EB] hover:bg-gray-50"
                        }
                    `}
                    >
                      <div
                        className={`flex items-center justify-center w-10 h-10 rounded-full font-bold transition-all duration-200
                        ${isSelected
                            ? `${s.darkColor} text-white ring-4 ring-white`
                            : `${s.lightColor} text-[#402B69]`
                          }
                      `}
                      >
                        {s.initials}
                      </div>

                      <span
                        className={`text-[15px] transition-colors duration-200
                        ${isSelected
                            ? "font-semibold text-black"
                            : "text-[#402B69]"
                          }
                      `}
                      >
                        {s.name}
                      </span>
                    </div>
                  );
                })
              )}
            </div>
            <div className="hidden lg:block absolute top-0 right-0 h-full w-[2px] bg-gray-200 shadow-[4px_0_12px_rgba(0,0,0,0.15)]"></div>
          </div>
        )}

        {/* Right Side - Amount */}
        <div className={`flex flex-col items-center justify-center p-6 sm:p-10 flex-shrink-0 ${quickPaymentType === "direct" ? "w-full" : "w-full lg:w-[62%]"
          }`}>
          <div className="w-full max-w-md flex flex-col items-center">
            <h3 className="text-[16px] font-medium text-[#402B69] mb-2">
              Enter Amount
            </h3>
            <p className="text-[28px] font-bold text-[#402B69] mb-6">£{amount}</p>

            {/* Keypad */}
            <div className="grid grid-cols-3 gap-4 mb-8 w-full max-w-xs">
              {["1", "2", "3", "4", "5", "6", "7", "8", "9", ".", "0", "del"].map(
                (k) => (
                  <button
                    key={k}
                    onClick={() => handleKey(k)}
                    className="w-20 h-14 border-2 border-gray-200 rounded-md text-[18px] font-semibold text-[#402B69] hover:bg-gray-100 transition flex items-center justify-center"
                  >
                    {k === "del" ? "⌫" : k}
                  </button>
                )
              )}
            </div>

            <div className="flex gap-4 w-full max-w-[280px]">
              <button
                onClick={async () => {
                  await handlePayment("cash");
                }}
                disabled={submitting || parseFloat(amount) <= 0}
                className="flex-1 h-[64px] bg-[#22C55E] text-white rounded-lg py-3 font-extrabold hover:bg-[#16a34a] transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submitting ? "..." : "CASH"}
              </button>
              <button
                onClick={async () => {
                  await handlePayment("card");
                }}
                disabled={submitting || parseFloat(amount) <= 0}
                className="flex-1 h-[64px] bg-[#885ABB] text-white rounded-lg py-3 font-extrabold hover:bg-[#6f3fa8] transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submitting ? "..." : "CARD"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
